/**
 * User Types
 * 
 * User management and authentication types
 */

import type { TradingSettings } from './tradingSettings.types';

/**
 * User Role
 */
export enum UserRole {
  GUEST = 'guest',      // 游客
  TRADER = 'trader',    // 交易者
  BROKER = 'broker',    // 交易商
  ADMIN = 'admin'       // 管理员
}

/**
 * User Information
 */
export interface User {
  uid: string;              // 用户唯一标识（UID，8位数字）- 主键
  userId?: string;          // 别名（部分 API 返回）
  username: string;         // 用户名（用于登录）
  displayName?: string;     // 显示名称
  email: string;
  role: UserRole;
  accountId: string;        // 关联到账户（同 uid）
  
  // 推荐码（新增）
  referralCode: string;           // 默认推荐码（6位字母数字）- 对外展示
  customReferralCode?: string;    // 自定义推荐码（可选）- 用户可自定义
  
  // 第三方登录绑定（新增）
  walletAddress?: string | null;  // MetaMask钱包地址
  googleId?: string | null;       // Google账号ID
  
  // 推荐人
  referrerId?: string;             // 推荐人UID（顶层别名）

  // 个人信息
  profile?: {
    referrerCode?: string;         // 推荐人的推荐码（不再是UID）
    referrerUid?: string;          // 推荐人的UID（内部使用，永久不变）
    referrerLockedAt?: number;     // 推荐人锁定时间（48小时后）
    hasSeenReferrerPopup?: boolean; // 是否已看过推荐人弹窗
    web3Address?: string;          // Web3钱包地址（已废弃，使用 walletAddress）
    googleAccount?: string;        // Google账号（已废弃，使用 email + googleId）
  };
  
  status?: string;                 // 账号状态（active / disabled 等）
  referrerInfo?: unknown | null;   // 推荐人信息（原始 API 数据）
  usedReferralCode?: string | null; // 注册时使用的推荐码

  createdAt: number;
  lastLoginAt: number;
  settings?: UserSettings;
  tradingSettings?: TradingSettings;  // 交易设置（由交易商配置）
}

/**
 * User Settings
 */
export interface UserSettings {
  // 交易设置
  defaultLeverage: number;        // 默认杠杆
  riskWarning: boolean;           // 风险提醒
  
  // 图表设置
  chartTheme: 'dark' | 'light';   // 图表主题
  colorScheme: 'green-red' | 'red-green';  // 涨跌色方案
  
  // 通知设置
  emailNotifications: boolean;    // 邮件通知
  priceAlerts: boolean;           // 价格提醒
}

/**
 * User Session
 */
export interface UserSession {
  sessionId: string;
  uid: string;              // 用户UID
  token: string;
  role: UserRole;
  expiresAt: number;
  remember: boolean;
  username?: string;        // 用户名（可能存在于 session 快照中）
}

/**
 * Login Credentials
 */
export interface LoginCredentials {
  username: string;
  password: string;
  remember?: boolean;
}

/**
 * Register Data
 */
export interface RegisterData {
  username: string;
  email: string;
  password: string;
  brokerUid?: string;    // 可选：交易商UID（8位数字，注册时填写，未填则使用默认）
  referrerCode?: string; // 可选：推荐人代码（6位字母数字，注册时填写）
}

/**
 * User With Account (完整的用户信息含账户)
 */
export interface UserWithAccount extends User {
  account: Account;
}

/**
 * Account Information
 */
export interface Account {
  accountId: string;        // 账户ID（同 uid）
  uid: string;              // 属于哪个用户
  
  // 余额信息
  balance: number;          // 总余额
  availableBalance: number; // 可用余额
  frozenMargin: number;     // 冻结保证金
  
  // 统计信息
  unrealizedPnl: number;    // 未实现盈亏
  realizedPnl: number;      // 已实现盈亏
  
  // 费率配置
  makerFeeRate?: number;    // Maker 费率 (默认 0.02%)
  takerFeeRate?: number;    // Taker 费率 (默认 0.05%)
  depositFeeRate?: number;  // 充值费率 (默认 2%)
  brokerRebateRate?: number; // 交易商回馈比例 (默认 1%)
  
  // 元数据
  createdAt: number;
  updatedAt: number;
}

/**
 * Transaction Type
 */
export enum TransactionType {
  // 存取款
  DEPOSIT = 'deposit',                    // 💰 存款
  WITHDRAWAL = 'withdrawal',              // 💸 取款
  
  // 费用
  DEPOSIT_FEE = 'deposit_fee',            // 📉 存款手续费（负数）
  TRADING_FEE = 'trading_fee',            // 📉 交易手续费（负数）
  
  // 回馈与奖励
  BROKER_REBATE_IN = 'broker_rebate_in',  // 🎁 交易商回馈（用户收入）
  BROKER_REBATE_OUT = 'broker_rebate_out',// 📤 交易商回馈（交易商支出）
  REFERRER_REWARD = 'referrer_reward',    // 🎁 推荐人奖励
  
  // 交易盈亏
  REALIZED_PNL = 'realized_pnl',          // 💹 已实现盈亏
  
  // 强平
  LIQUIDATION = 'liquidation'             // ⚠️ 强制平仓
}

/**
 * Transaction Status
 */
export enum TransactionStatus {
  PENDING = 'pending',      // 待处理
  COMPLETED = 'completed',  // 成功
  FAILED = 'failed'         // 失败
}

/**
 * Transaction Record
 */
export interface Transaction {
  transactionId: string;      // 交易ID（唯一）
  uid: string;                // 用户UID
  type: TransactionType;      // 交易类型
  
  // 金额信息
  amount: number;             // 金额（正数=收入，负数=支出）
  fee?: number;               // 手续费（如果有）
  balanceBefore: number;      // 交易前余额
  balanceAfter: number;       // 交易后余额
  
  // 状态
  status: TransactionStatus;  // 状态
  
  // 元数据
  metadata?: {
    // 存款相关
    paymentMethod?: string;      // 支付方式
    depositAddress?: string;     // 存款地址
    txHash?: string;             // 交易哈希
    
    // 取款相关
    walletAddress?: string;      // 钱包地址
    
    // 回馈相关
    brokerUid?: string;          // 交易商UID
    clientUid?: string;          // 客户UID
    rebateRate?: number;         // 回馈比例
    rebateWindowHours?: number;  // 时间窗口（小时）
    rebateMaxAmount?: number;    // 最大金额限制
    rebateUsedAmount?: number;   // 已使用金额
    
    // 推荐人相关
    referrerUid?: string;        // 推荐人UID
    referredUid?: string;        // 被推荐人UID
    rewardRate?: number;         // 奖励比例
    depositFee?: number;         // 存款费用
    
    // 交易相关
    orderId?: string;            // 订单ID
    symbol?: string;             // 交易对
    side?: string;               // 方向
    
    // 备注
    notes?: string;              // 备注
    errorMessage?: string;       // 错误信息
  };
  
  createdAt: number;           // 创建时间
  updatedAt?: number;          // 更新时间
}

/**
 * Client Deposit Record (for Broker)
 */
export interface ClientDepositRecord {
  recordId: string;            // 记录ID
  brokerUid: string;           // 交易商UID
  clientUid: string;           // 客户UID
  clientUsername: string;      // 客户用户名
  
  // 存款信息
  depositAmount: number;       // 存款金额
  depositFee: number;          // 存款手续费
  actualAmount: number;        // 实际到账
  
  // 回馈信息
  rebateAmount: number;        // 回馈金额
  rebateStatus: 'pending' | 'completed' | 'partial' | 'failed' | 'limit_reached';
  rebateErrorMessage?: string; // 回馈失败原因
  
  // 时间
  depositTime: number;         // 存款时间
  rebateTime?: number;         // 回馈时间
  
  // 状态
  isRead: boolean;             // 是否已读
  
  createdAt: number;
}

/**
 * Permission Configuration
 */
export interface Permission {
  role: UserRole;
  pages: string[];          // 可访问页面
  features: string[];       // 可用功能
}

/**
 * Default Permissions
 */
export const PERMISSIONS: Record<UserRole, Permission> = {
  [UserRole.GUEST]: {
    role: UserRole.GUEST,
    pages: ['chart.html'],
    features: ['view_chart', 'add_indicator']
  },
  
  [UserRole.TRADER]: {
    role: UserRole.TRADER,
    pages: ['chart.html', 'trader.html', 'user.html'],
    features: [
      'view_chart',
      'add_indicator',
      'place_order',
      'manage_position',
      'view_orderbook',
      'view_trades'
    ]
  },
  
  [UserRole.BROKER]: {
    role: UserRole.BROKER,
    pages: ['chart.html', 'trader.html', 'broker.html', 'user.html'],
    features: [
      'view_chart',
      'place_order',
      'manage_clients',
      'setup_hedging',
      'view_statistics',
      'view_risk_monitor'
    ]
  },
  
  [UserRole.ADMIN]: {
    role: UserRole.ADMIN,
    pages: ['*'],  // All pages
    features: ['*']  // All features
  }
};

